'use client'

import { auth } from './firebase'
import { deviceFingerprintService } from './device-fingerprint'
import {
  setNinxKeys,
  setFcmToken,
  setNinxUserInfo,
  clearNinxSession,
  hasValidNinxSession,
  type NinxAuthResponse
} from './auth-cookies'
import { sessionRefreshManager } from './session-refresh'

/**
 * NINX Authentication Service
 * Handles authentication with the NINX Deno backend
 */

const DENO_BASE_URL =
  process.env.NEXT_PUBLIC_DENO_URL || 'https://nin-engine.deno.dev'

interface NinxLoginRequest {
  uid?: string
  email?: string
  username?: string
  country?: string
  phoneNumber?: string
}

class NinxAuthService {
  private static instance: NinxAuthService
  private fcmToken: string | null = null

  private constructor() {
    // Initialize FCM token if available
    this.initializeFcmToken()
  }

  public static getInstance(): NinxAuthService {
    if (!NinxAuthService.instance) {
      NinxAuthService.instance = new NinxAuthService()
    }
    return NinxAuthService.instance
  }

  /**
   * Initialize FCM token (placeholder for now)
   * In a real app, you would initialize Firebase messaging here
   */
  private async initializeFcmToken(): Promise<void> {
    try {
      // For now, use a mock FCM token
      // In production, you would get this from Firebase messaging
      this.fcmToken =
        'FCM___gwlS_vLWFbwYcf7ExRFclybMO-FPO1nFiGfNwm4On2TZHYv7oblK7v3N5kYqemBNn_u8XapzBKUd4wd7OSeV6AELdrfYgdA'

      if (this.fcmToken) {
        setFcmToken(this.fcmToken)
      }
    } catch (error) {
      console.error('Error initializing FCM token:', error)
    }
  }

  /**
   * Get Firebase auth token
   */
  private async getFirebaseToken(): Promise<string | null> {
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('No authenticated user found')
      }
      return await currentUser.getIdToken()
    } catch (error) {
      console.error('Error getting Firebase token:', error)
      return null
    }
  }

  /**
   * Get common headers for NINX API requests
   */
  private async getHeaders(
    providedFingerprint?: string
  ): Promise<Record<string, string>> {
    const deviceFingerprint =
      providedFingerprint ??
      (await deviceFingerprintService.getFingerprintHeader())

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-device-fingerprint': deviceFingerprint
    }

    // Add FCM token if available
    if (this.fcmToken) {
      headers['x-fcm-token'] = this.fcmToken
    }

    return headers
  }

  /**
   * Login to NINX backend using Firebase token
   */
  public async loginWithFirebase(
    fingerprintHeader?: string
  ): Promise<NinxAuthResponse | null> {
    const isDev = process.env.NODE_ENV === 'development'

    try {
      // if (isDev) console.log('[NINX Auth] Attempting login with Firebase token...')

      const firebaseToken = await this.getFirebaseToken()
      if (!firebaseToken) {
        throw new Error('Failed to get Firebase authentication token')
      }

      const headers = await this.getHeaders(fingerprintHeader)
      headers['Authorization'] = `Bearer ${firebaseToken}`

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const authResponse: NinxAuthResponse = await response.json()

      if (!authResponse.status) {
        throw new Error(authResponse.message || 'Login failed')
      }

      if (!authResponse.already_exists) {
        if (isDev)
          console.log('[NINX Auth] User does not exist, signup required')
        return authResponse
      }

      await this.storeSessionData(authResponse)

      if (isDev) console.log('[NINX Auth] Login successful')
      return authResponse
    } catch (error) {
      console.error('[NINX Auth] Login failed:', error)
      return null
    }
  }

  /**
   * Refresh NINX session using Firebase token
   * This calls the backend refresh endpoint which updates both frontend and backend session cookies
   */
  public async refreshSession(
    fingerprintHeader?: string
  ): Promise<NinxAuthResponse | null> {
    const isDev = process.env.NODE_ENV === 'development'

    try {
      if (isDev) console.log('[NINX Auth] Attempting session refresh...')

      const firebaseToken = await this.getFirebaseToken()
      if (!firebaseToken) {
        throw new Error('Failed to get Firebase authentication token')
      }

      const headers = await this.getHeaders(fingerprintHeader)
      headers['Authorization'] = `Bearer ${firebaseToken}`

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        )
      }

      const authResponse: NinxAuthResponse = await response.json()

      if (!authResponse.status) {
        throw new Error(authResponse.message || 'Session refresh failed')
      }

      // Update session data with refreshed values
      await this.storeSessionData(authResponse)

      if (isDev) console.log('[NINX Auth] Session refresh successful')
      return authResponse
    } catch (error) {
      console.error('[NINX Auth] Session refresh failed:', error)
      return null
    }
  }

  /**
   * Store session data from successful login or refresh
   * Backend-managed cookies (sessionId) are handled via API responses
   */
  private async storeSessionData(
    authResponse: NinxAuthResponse
  ): Promise<void> {
    try {
      // Backend session is managed via the sessionId cookie (forwarded by the API route)

      // Store API keys only if they exist and are not empty
      if (
        authResponse.ninx_keys &&
        authResponse.ninx_keys.api_key &&
        authResponse.ninx_keys.api_secret
      ) {
        setNinxKeys(
          authResponse.ninx_keys.api_key,
          authResponse.ninx_keys.api_secret
        )
      } else {
        console.log('No NINX keys provided - user may need to complete KYC')
      }

      // Store user info
      setNinxUserInfo(
        authResponse.ninx_kyc_status,
        authResponse.ninx_kyc_level,
        authResponse.ninx_vip_level
      )

      // Start session refresh monitoring with the expiry time from backend
      console.log(
        `🔄 Starting session refresh monitoring with ${authResponse.data.expiresIn}ms expiry`
      )
      sessionRefreshManager.startSessionRefresh(authResponse.data.expiresIn)

      // Register NINX connector with ConnectorManager after authentication
      if (typeof window !== 'undefined') {
        try {
          const { ConnectorManager } = require('../components/Connectors/ConnectorManager')
          const connectorManager = ConnectorManager.getInstance()
          connectorManager.registerNinxConnector()
          console.log('NINX connector registered with ConnectorManager')
        } catch (error) {
          console.error('Failed to register NINX connector:', error)
        }
      }

      console.log('Session data stored successfully')
    } catch (error) {
      console.error('Error storing session data:', error)
      throw error
    }
  }

  /**
   * Check if user has valid NINX session
   */
  public isAuthenticated(): boolean {
    return hasValidNinxSession()
  }

  /**
   * Auto-authenticate if Firebase user exists and no NINX session
   */
  public async autoAuthenticate(): Promise<boolean> {
    const isDev = process.env.NODE_ENV === 'development'

    try {
      // Check if already authenticated
      if (this.isAuthenticated()) {
        // if (isDev) console.log('[NINX Auth] Session already exists');
        return true
      }

      // Check if Firebase user exists
      const currentUser = auth.currentUser
      if (!currentUser) {
        // if (isDev) console.log('[NINX Auth] No Firebase user found for auto-authentication');
        return false
      }

      // Attempt login
      const result = await this.loginWithFirebase()
      return result !== null
    } catch (error) {
      console.error('[NINX Auth] Auto-authentication failed:', error)
      return false
    }
  }

  /**
   * Make authenticated request to NINX API
   */
  public async makeAuthenticatedRequest(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Response> {
    try {
      const headers = await this.getHeaders()

      const response = await fetch(`${DENO_BASE_URL}/api/v1${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      })

      // Handle session expiry with reactive refresh
      if (response.status === 401) {
        console.log('[NINX Auth] 401 detected - attempting session refresh...')

        // Try to refresh the session
        const refreshResult = await this.refreshSession()
        if (refreshResult && refreshResult.status) {
          console.log(
            '[NINX Auth] Session refreshed, retrying original request...'
          )
          // Retry the original request with refreshed session
          return await fetch(`${DENO_BASE_URL}/api/v1${endpoint}`, {
            ...options,
            headers: {
              ...headers,
              ...options.headers
            }
          })
        } else {
          // Refresh failed, clear session
          console.error('[NINX Auth] Session refresh failed, clearing session')
          clearNinxSession()
        }
      }

      return response
    } catch (error) {
      console.error('Authenticated request failed:', error)
      throw error
    }
  }

  /**
   * Update FCM token
   */
  public updateFcmToken(token: string): void {
    this.fcmToken = token
    setFcmToken(token)
  }

  /**
   * Logout from NINX backend
   */
  public async logout(): Promise<boolean> {
    try {
      // Logging out from NINX
      clearNinxSession()
      return true
    } catch (error) {
      console.error('[NINX Auth] Logout failed:', error)
      return false
    }
  }
}

// Export singleton instance
export const ninxAuthService = NinxAuthService.getInstance()

// Export types
export type { NinxLoginRequest }
